/**
 * Add Registry Form Component
 * Dialog form to add a new registry URL
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type {
  CreateRegistryRequest,
  RegistryPlatform,
  RegistryPlatformInfo
} from '@/types/celebrationRegistry.types';

interface AddRegistryFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateRegistryRequest) => Promise<void>;
  preselectedPlatform?: RegistryPlatformInfo;
  platforms: RegistryPlatformInfo[];
  isLoading?: boolean;
}

export const AddRegistryForm: React.FC<AddRegistryFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  preselectedPlatform,
  platforms,
  isLoading
}) => {
  const { t } = useTranslation(['celebrations', 'common']);
  const [formData, setFormData] = useState<CreateRegistryRequest>({
    registry_name: preselectedPlatform ? `Our ${preselectedPlatform.name}` : '',
    registry_url: '',
    registry_platform: preselectedPlatform?.platform as RegistryPlatform,
    is_primary: false,
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onClose = () => onOpenChange(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.registry_name.trim()) {
      setError(t('celebrations:registry.addForm.errors.nameRequired'));
      return;
    }

    if (!formData.registry_url.trim()) {
      setError(t('celebrations:registry.addForm.errors.urlRequired'));
      return;
    }

    // Basic URL validation
    try {
      new URL(formData.registry_url);
    } catch {
      setError(t('celebrations:registry.addForm.errors.invalidUrl'));
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(formData);
      setFormData({
        registry_name: preselectedPlatform ? `Our ${preselectedPlatform.name}` : '',
        registry_url: '',
        registry_platform: preselectedPlatform?.platform as RegistryPlatform,
        is_primary: false,
        notes: ''
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('celebrations:registry.addForm.errors.addFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('celebrations:registry.addForm.title')}</DialogTitle>
          <DialogDescription>
            {t('celebrations:registry.addForm.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Registry Name */}
          <div className="space-y-2">
            <Label htmlFor="registry_name">{t('celebrations:registry.addForm.labels.registryName')}</Label>
            <Input
              id="registry_name"
              placeholder={t('celebrations:registry.addForm.placeholders.registryName')}
              value={formData.registry_name}
              onChange={(e) =>
                setFormData({ ...formData, registry_name: e.target.value })
              }
              disabled={isSubmitting || isLoading}
            />
          </div>

          {/* Registry URL */}
          <div className="space-y-2">
            <Label htmlFor="registry_url">{t('celebrations:registry.addForm.labels.registryUrl')}</Label>
            <Input
              id="registry_url"
              type="url"
              placeholder={t('celebrations:registry.addForm.placeholders.registryUrl')}
              value={formData.registry_url}
              onChange={(e) =>
                setFormData({ ...formData, registry_url: e.target.value })
              }
              disabled={isSubmitting || isLoading}
            />
          </div>

          {/* Platform (optional) */}
          <div className="space-y-2">
            <Label htmlFor="platform">{t('celebrations:registry.addForm.labels.platformOptional')}</Label>
            <select
              id="platform"
              value={formData.registry_platform || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  registry_platform: e.target.value as RegistryPlatform
                })
              }
              disabled={isSubmitting || isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t('celebrations:registry.addForm.options.autoDetect')}</option>
              {platforms.map((p) => (
                <option key={p.platform} value={p.platform}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('celebrations:registry.addForm.labels.notesOptional')}</Label>
            <Textarea
              id="notes"
              placeholder={t('celebrations:registry.addForm.placeholders.notes')}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              disabled={isSubmitting || isLoading}
              rows={3}
            />
          </div>

          {/* Set as Primary */}
          <div className="flex items-center gap-2">
            <input
              id="is_primary"
              type="checkbox"
              checked={formData.is_primary || false}
              onChange={(e) =>
                setFormData({ ...formData, is_primary: e.target.checked })
              }
              disabled={isSubmitting || isLoading}
            />
            <Label htmlFor="is_primary" className="flex-1 cursor-pointer">
              {t('celebrations:registry.addForm.labels.setPrimary')}
            </Label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Form Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting || isLoading}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting ? t('celebrations:registry.addForm.actions.adding') : t('celebrations:registry.addForm.actions.addRegistry')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
